import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import client from "../api/client";
import Modal from "../Moodboard/Modal";
import { formatMoney } from "../Architect/units";
import CustomerDialog from "./CustomerDialog";
import { STATUS_LABELS, formatDate, stateLabel } from "./gst";
import "../Moodboard/Moodboard.css";
import "../Architect/Architect.css";
import "./Quotations.css";

export function StatusBadge({ status, expired }) {
  const key = expired ? "expired" : status;
  return <span className={`qt-status is-${key}`}>{STATUS_LABELS[key] || key}</span>;
}

// A list that searches the server as you type.
function useSearch(url, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(params);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await client.get(url, { params: JSON.parse(key) });
      setData(response.data);
    } catch {
      toast.error("Could not load the list.", { toastId: url });
    } finally {
      setLoading(false);
    }
  }, [url, key]);
  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);
  return { data, loading, reload: load };
}

function NewQuotationDialog({ onClose, onCreated }) {
  const [mode, setMode] = useState("customer");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const url = mode === "customer" ? "/api/admin/customers" : "/api/admin/projects";
  const { data, loading } = useSearch(url, { search: search || undefined });
  const rows = mode === "customer" ? data?.items || [] : data || [];

  const create = async (body) => {
    setBusy(true);
    try {
      const { data: quote } = await client.post("/api/admin/quotations", body);
      onCreated(quote);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not create the quotation.");
      setBusy(false);
    }
  };

  if (creatingCustomer) {
    return <CustomerDialog onClose={() => setCreatingCustomer(false)} onSaved={(customer) => create({ customer_id: customer.id })} />;
  }

  return (
    <Modal title="New quotation" onClose={onClose}>
      <div className="ar-form">
        <div className="ar-segmented" role="radiogroup" aria-label="Start from">
          {[
            ["customer", "For a customer"],
            ["project", "From an architect's project"],
          ].map(([key, label]) => (
            <button key={key} type="button" role="radio" aria-checked={mode === key} className={mode === key ? "active" : ""} onClick={() => setMode(key)}>
              {label}
            </button>
          ))}
        </div>
        <label className="ar-search">
          <Search size={15} strokeWidth={1.8} aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={mode === "customer" ? "Search customers" : "Search projects, clients or architects"}
            autoFocus
          />
        </label>
        {loading && !rows.length ? (
          <p className="ar-muted">Loading...</p>
        ) : (
          <ul className="ar-project-choices">
            {rows.map((row) => (
              <li key={row.id}>
                <button type="button" disabled={busy} onClick={() => create(mode === "customer" ? { customer_id: row.id } : { project_id: row.id })}>
                  <strong>{mode === "customer" ? row.company || row.name : row.name}</strong>
                  <span>
                    {mode === "customer"
                      ? [row.company ? row.name : null, stateLabel(row.state_code), row.gstin].filter(Boolean).join(" · ")
                      : [row.owner, row.client_name].filter(Boolean).join(" · ")}
                  </span>
                </button>
              </li>
            ))}
            {!rows.length && <p className="ar-muted">{mode === "customer" ? "No customers found." : "No projects found."}</p>}
          </ul>
        )}
        <div className="mb-modal-actions">
          {mode === "customer" && (
            <button type="button" className="mb-tool" onClick={() => setCreatingCustomer(true)} disabled={busy}>
              <Plus size={15} strokeWidth={2} aria-hidden="true" />
              New customer
            </button>
          )}
          <button type="button" className="mb-text-button" onClick={() => create({})} disabled={busy}>
            Blank quotation
          </button>
        </div>
      </div>
    </Modal>
  );
}

function QuotationsTab() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [fy, setFy] = useState("");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const { data, loading } = useSearch("/api/admin/quotations", { status, search: search || undefined, fy: fy || undefined, page });
  const counts = data?.counts || {};
  const all = ["draft", "sent", "accepted", "rejected"].reduce((sum, key) => sum + (counts[key] || 0), 0);

  return (
    <>
      <div className="qt-toolbar">
        <div className="ar-tabs" role="tablist" aria-label="Filter by status">
          {["all", "draft", "sent", "accepted", "rejected", "expired"].map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={status === key}
              className={status === key ? "active" : ""}
              onClick={() => {
                setStatus(key);
                setPage(1);
              }}
            >
              {key === "all" ? "All" : STATUS_LABELS[key]}
              <span>{key === "all" ? all : counts[key] || 0}</span>
            </button>
          ))}
        </div>
        <div className="ar-head-actions">
          <label className="ar-search">
            <Search size={15} strokeWidth={1.8} aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Number, subject or customer"
            />
          </label>
          <select
            value={fy}
            onChange={(event) => {
              setFy(event.target.value);
              setPage(1);
            }}
            aria-label="Financial year"
          >
            <option value="">All years</option>
            {(data?.years || []).map((year) => (
              <option key={year} value={year}>
                FY {year}
              </option>
            ))}
          </select>
          <button type="button" className="mb-primary" onClick={() => setCreating(true)}>
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
            New quotation
          </button>
        </div>
      </div>

      {loading && !data ? (
        <p className="mb-muted">Loading quotations...</p>
      ) : !data?.items.length ? (
        <section className="mb-empty">
          <h2>{search || status !== "all" || fy ? "No quotations match" : "Create your first quotation"}</h2>
          <p>Quotations carry your letterhead, GST worked out per line, and can be emailed to the customer as a PDF.</p>
        </section>
      ) : (
        <div className="qt-table-wrap">
          <table className="qt-table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Customer</th>
                <th>Subject</th>
                <th>Date</th>
                <th>Valid until</th>
                <th className="is-num">Total (INR)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((quote) => (
                <tr key={quote.id} onClick={() => navigate(`/admin/quotations/${quote.id}`)} tabIndex={0} onKeyDown={(event) => event.key === "Enter" && navigate(`/admin/quotations/${quote.id}`)}>
                  <td className="qt-number">{quote.display_number}</td>
                  <td>{quote.customer_name || <span className="ar-muted">No customer</span>}</td>
                  <td className="qt-subject">{quote.subject || ""}</td>
                  <td>{formatDate(quote.quote_date)}</td>
                  <td>{formatDate(quote.valid_until)}</td>
                  <td className="is-num">{formatMoney(quote.grand_total)}</td>
                  <td>
                    <StatusBadge status={quote.status} expired={quote.expired} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.total_pages > 1 && (
        <div className="ar-pager">
          <button type="button" className="mb-tool" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
            Previous
          </button>
          <span>
            Page {page} of {data.total_pages}
          </span>
          <button type="button" className="mb-tool" disabled={page >= data.total_pages} onClick={() => setPage((value) => value + 1)}>
            Next
          </button>
        </div>
      )}
      {creating && <NewQuotationDialog onClose={() => setCreating(false)} onCreated={(quote) => navigate(`/admin/quotations/${quote.id}`)} />}
    </>
  );
}

function CustomersTab() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const { data, loading, reload } = useSearch("/api/admin/customers", { search: search || undefined, page });

  const remove = async (customer) => {
    if (!window.confirm(`Delete ${customer.company || customer.name}?`)) return;
    try {
      await client.delete(`/api/admin/customers/${customer.id}`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not delete the customer.");
    }
  };

  return (
    <>
      <div className="qt-toolbar">
        <label className="ar-search">
          <Search size={15} strokeWidth={1.8} aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Name, company, phone, email or GSTIN"
          />
        </label>
        <button type="button" className="mb-primary" onClick={() => setEditing({})}>
          <Plus size={16} strokeWidth={2} aria-hidden="true" />
          New customer
        </button>
      </div>
      {loading && !data ? (
        <p className="mb-muted">Loading customers...</p>
      ) : !data?.items.length ? (
        <section className="mb-empty">
          <h2>{search ? "No customers match" : "No customers yet"}</h2>
          <p>Add customers here, or fill one in from a registered architect or designer.</p>
        </section>
      ) : (
        <div className="qt-table-wrap">
          <table className="qt-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>GSTIN</th>
                <th>State</th>
                <th>Contact</th>
                <th className="is-num">Quotations</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((customer) => (
                <tr key={customer.id} className="is-static">
                  <td>
                    <strong>{customer.company || customer.name}</strong>
                    {customer.company && <div className="ar-muted ar-small">{customer.name}</div>}
                  </td>
                  <td>{customer.gstin || "—"}</td>
                  <td>{customer.state || "—"}</td>
                  <td className="ar-small">{[customer.phone, customer.email].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="is-num">{customer.quotation_count}</td>
                  <td className="qt-actions">
                    <button type="button" className="mb-icon-button" onClick={() => setEditing(customer)} aria-label={`Edit ${customer.name}`} title="Edit">
                      <Pencil size={14} strokeWidth={1.8} />
                    </button>
                    <button type="button" className="mb-icon-button danger" onClick={() => remove(customer)} aria-label={`Delete ${customer.name}`} title="Delete">
                      <Trash2 size={14} strokeWidth={1.8} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.total_pages > 1 && (
        <div className="ar-pager">
          <button type="button" className="mb-tool" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
            Previous
          </button>
          <span>
            Page {page} of {data.total_pages}
          </span>
          <button type="button" className="mb-tool" disabled={page >= data.total_pages} onClick={() => setPage((value) => value + 1)}>
            Next
          </button>
        </div>
      )}
      {editing && (
        <CustomerDialog
          customer={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </>
  );
}

export default function QuotationList() {
  const [tab, setTab] = useState(() => (window.location.hash === "#customers" ? "customers" : "quotations"));
  const choose = (next) => {
    setTab(next);
    window.history.replaceState(null, "", next === "customers" ? "#customers" : window.location.pathname);
  };
  return (
    <main className="mb-page ar-page">
      <ToastContainer position="bottom-right" />
      <header className="mb-page-head">
        <div>
          <p className="mb-kicker">Administration</p>
          <h1>Quotations</h1>
        </div>
        <div className="ar-segmented" role="tablist" aria-label="Section">
          {[
            ["quotations", "Quotations"],
            ["customers", "Customers"],
          ].map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? "active" : ""} onClick={() => choose(key)}>
              {label}
            </button>
          ))}
        </div>
      </header>
      {tab === "quotations" ? <QuotationsTab /> : <CustomersTab />}
    </main>
  );
}
